/**
 * Types for Correction Invoice functionality.
 * 
 * @remarks
 * These types mirror backend DTOs for correction invoices.
 * Used for displaying before/after comparison and calculating differences.
 */

import { VatRateType } from './invoicesTypes';

/**
 * Represents the differences between a base invoice and its correction.
 * Used for displaying before/after comparison in UI and generating PDF reports.
 */
export interface CorrectionDiff {
  /**
   * Invoice number of the base (original) invoice being corrected.
   * Example: "FV/100/01/2025"
   */
  baseInvoiceNumber: string;

  /**
   * Issue date of the base invoice.
   * Format: ISO 8601 (YYYY-MM-DDTHH:mm:ssZ)
   */
  baseIssueDate: string;

  /**
   * List of item-level differences (before/after/delta for each position).
   */
  items: CorrectionItemDiff[];

  /**
   * Total net amount BEFORE correction.
   */
  totalNetBefore: number;

  /**
   * Total net amount AFTER correction.
   */
  totalNetAfter: number;

  /**
   * Net amount delta (after - before).
   * Positive = increase, Negative = decrease, Zero = no change.
   */
  totalNetDelta: number;

  /**
   * Total VAT amount BEFORE correction.
   */
  totalVatBefore: number;

  /**
   * Total VAT amount AFTER correction.
   */
  totalVatAfter: number;

  /**
   * VAT amount delta (after - before).
   */
  totalVatDelta: number;

  /**
   * Total gross amount BEFORE correction.
   */
  totalGrossBefore: number;

  /**
   * Total gross amount AFTER correction.
   */
  totalGrossAfter: number;

  /**
   * Gross amount delta (after - before).
   */
  totalGrossDelta: number;

  /**
   * List of VAT rate-level differences (delta per rate: 23%, 8%, ZW, NP, etc.).
   * Shows how much changed for each VAT rate type.
   * NEW: Added to match backend CorrectionDiffDto
   */
  vatRateDiffs: VatRateDiff[];
}

/**
 * Represents the differences for a single invoice item (line position).
 */
export interface CorrectionItemDiff {
  /**
   * Item name (used as matching key between base and correction).
   * Example: "Hotel XYZ - 7 nights"
   */
  name: string;

  /**
   * Quantity BEFORE correction.
   * 0 if item was added in correction (didn't exist in base).
   */
  quantityBefore: number;

  /**
   * Quantity AFTER correction.
   * 0 if item was removed in correction (existed in base, not in correction).
   */
  quantityAfter: number;

  /**
   * Unit net price BEFORE correction.
   * 0 if item was added in correction.
   */
  unitNetBefore: number;

  /**
   * Unit net price AFTER correction.
   * 0 if item was removed in correction.
   */
  unitNetAfter: number;

  /**
   * Total net amount for this item BEFORE correction (quantityBefore * unitNetBefore).
   */
  totalNetBefore: number;

  /**
   * Total net amount for this item AFTER correction (quantityAfter * unitNetAfter).
   */
  totalNetAfter: number;

  /**
   * Net amount delta for this item (totalNetAfter - totalNetBefore).
   * Positive = increase, Negative = decrease, Zero = no change.
   */
  totalNetDelta: number;
}

/**
 * Represents VAT rate-level differences in a correction invoice.
 * Shows delta (change) for each VAT rate type.
 * NEW: Matches backend VatRateDiffDto
 */
export interface VatRateDiff {
  /**
   * VAT rate type (e.g., Vat23, Vat8, VatZW, VatNP).
   */
  vatType: VatRateType;

  /**
   * Net amount difference for this VAT rate (delta = after - before).
   */
  netDelta: number;

  /**
   * VAT amount difference for this VAT rate (delta = after - before).
   */
  vatDelta: number;

  /**
   * Gross amount difference for this VAT rate (delta = after - before).
   */
  grossDelta: number;
}

/**
 * Type guard to check if an invoice is a correction.
 */
export function isCorrectionInvoice(invoice: { type: string }): boolean {
  return invoice.type === 'correction';
}

/**
 * Type guard to check if diff has changes.
 */
export function hasChanges(diff: CorrectionDiff): boolean {
  return diff.totalNetDelta !== 0 || diff.totalVatDelta !== 0;
}

/**
 * Helper to determine delta color for UI.
 * @returns MUI color palette key
 */
export function getDeltaColor(value: number): 'error' | 'success' | 'text.secondary' {
  if (value > 0) return 'error'; // Red for increase
  if (value < 0) return 'success'; // Green for decrease
  return 'text.secondary'; // Gray for no change
}

/**
 * Helper to format delta with sign.
 */
export function formatDelta(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}`;
}
